import {
  hashKey,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

/**
 * Janela de agrupamento das invalidações que o socket pede (SA-1452).
 *
 * Uma rajada de eventos (campanha, encerramento em lote, IA respondendo em
 * várias conversas) pedia uma recarga por evento: cada uma cancelava a
 * anterior no navegador, mas o servidor processava todas. Agora o primeiro
 * pedido de uma chave abre a janela e a invalidação sai 1,5 s depois dele,
 * com o que chegou no meio junto. A busca começa depois do último evento da
 * janela, então nada se perde; e não é `cancelRefetch: false`, que deixava a
 * busca que já estava em voo "resolver" um evento que ela não viu (SA-1252).
 */
export const REALTIME_INVALIDATION_DELAY_MS = 1500;

type Pending = { timer: ReturnType<typeof setTimeout>; run: () => void };

// Uma fila por QueryClient: o app tem um só, os testes criam vários.
const queues = new WeakMap<QueryClient, Map<string, Pending>>();

const queueOf = (queryClient: QueryClient) => {
  let queue = queues.get(queryClient);
  if (!queue) {
    queue = new Map();
    queues.set(queryClient, queue);
  }
  return queue;
};

export function scheduleCoalescedInvalidation(
  queryClient: QueryClient,
  queryKey: QueryKey,
  options: { exact?: boolean } = {}
): void {
  const queue = queueOf(queryClient);
  const id = `${hashKey(queryKey)}|${options.exact ? "exact" : "prefix"}`;
  if (queue.has(id)) return;

  const run = () => {
    queue.delete(id);
    void queryClient.invalidateQueries({ queryKey, exact: options.exact });
  };
  queue.set(id, {
    timer: setTimeout(run, REALTIME_INVALIDATION_DELAY_MS),
    run,
  });
}

/** Roda já o que está pendente (testes e quem precisa da verdade agora). */
export function flushCoalescedInvalidations(queryClient: QueryClient): void {
  const queue = queues.get(queryClient);
  if (!queue) return;
  for (const pending of [...queue.values()]) {
    clearTimeout(pending.timer);
    pending.run();
  }
}

/** Descarta o que estava pendente sem invalidar (logout, troca de conta). */
export function resetCoalescedInvalidations(queryClient: QueryClient): void {
  const queue = queues.get(queryClient);
  if (!queue) return;
  for (const pending of queue.values()) clearTimeout(pending.timer);
  queue.clear();
}
